import { Box, Stack, Typography } from "@mui/material";
import { useQueryMedia } from "../../../../hooks/useQueryMedia";
import Colors from "../../../../utils/Colors";
import Images from "../../../../utils/Images";
import { CreateImage, boldText } from "./HowItWorkComponents";

export default function HowItWorkStudent() {
  const { isMobile } = useQueryMedia();

  return (
    <Stack spacing={2} sx={{ color: "white", pb: 2 }}>
      <Wrapper>
        {isMobile ? (
          <>
            <CreateImage width={18} image={Images.NEW_REMOTE_MOB} />
            <>
              <CreateTitleMob textAlign="left" title="Student-Teacher Interaction" />
              <CreateTextMob>
                The main mode of interaction during the class is through the remote control.
                <br />
                <br />
                It provides an anonymous and instant feedback to the teacher. To make sure that the feedback is minimally disruptive, the option to post questions/answers is disabled during the
                lecture. However, the teacher can enable it at any time.
              </CreateTextMob>
            </>
          </>
        ) : (
          <Stack direction="row" spacing={2}>
            <CreateImage width={18} image={Images.NEW_REMOTE_WEB} />
            <Stack spacing={2}>
              <CreateTitleWeb title="Student-Teacher Interaction" />
              <CreateTextWeb>
                The main mode of interaction during the class is through the remote control.
                <br />
                <br />
                It provides an anonymous and instant feedback to the teacher. To make sure that the feedback is minimally disruptive, the option to post questions/answers is disabled during the
                lecture. However, the teacher can enable it at any time.
              </CreateTextWeb>
            </Stack>
          </Stack>
        )}
      </Wrapper>

      <Wrapper>
        {isMobile ? (
          <>
            <CreateTitleMob title="Questions, Answers & Discussions (Q/A/D)" />
            <CreateTextMob>
              Unlike other platforms where posting under a pseudonym is an option, in our platform a student can post a Q/A/D only under a pseudonym. If you did not attend a specific lecture (you
              don’t have a pseudonym for that lecture), you cannot make a post (or respond to the post) for that lecture, but you could view all the posts (Q/A/D).
              <br />
              <br />
              To discourage turning questions and answers into discussions, the number of answers to each question is limited. If you would like to engage in a discussion, you can do so by making a
              comment on a specific answer. To avoid endless threads that involve multiple people, only two people can be involved in a discussion.
              <br />
              <br />
              Once posted, a post is visible to all students, but it becomes visible to the teacher only after it is seen by a sufficient number of students.
            </CreateTextMob>
          </>
        ) : (
          <>
            <CreateTitleWeb title="Questions, Answers & Discussions (Q/A/D)" />
            <CreateTextWeb>
              Unlike other platforms where posting under a pseudonym is an option, in our platform a student can post a Q/A/D only under a pseudonym. If you did not attend a specific lecture (you
              don’t have a pseudonym for that lecture), you cannot make a post (or respond to the post) for that lecture, but you could view all the posts (Q/A/D).
              <br />
              <br />
              To discourage turning questions and answers into discussions, the number of answers to each question is limited. If you would like to engage in a discussion, you can do so by making a
              comment on a specific answer. To avoid endless threads that involve multiple people, only two people can be involved in a discussion.
              <br />
              <br />
              Once posted, a post is visible to all students, but it becomes visible to the teacher only after it is seen by a sufficient number of students.
            </CreateTextWeb>
          </>
        )}
      </Wrapper>

      <Wrapper>
        {isMobile ? (
          <>
            <CreateTitleMob title="Flagging and Reputation" />
            <CreateTextMob>
              Flags are used to label questions or answers (posts) that have inappropriate or offensive content and should not be used just because a user does not agree with the content of the post.
              If a post receives enough flags, it is moved to a ‘doghouse’. All the posts in the doghouse are visible to all the students but not to the teacher. Once a student presses a flag button,
              the flag color changes to red only on that student’s page.
              <br />
              <br />
              A flagged post can be moved back to the regular Q&A area if enough students do not think that the post should be in the doghouse. Every time a post goes to the doghouse, the reputation
              of the person who made a post is reduced. A person with a sufficiently low reputation is not allowed to make a new post. However, reputation can be regained over time. All this is
              calculated by the algorithm.
              <br />
              <br />
              Please use flagging judiciously. If you flag to many posts and many other users disagree with you, your reputation might be decreased.
            </CreateTextMob>
          </>
        ) : (
          <>
            <CreateTitleWeb title="Flagging and Reputation" />
            <CreateTextWeb>
              Flags are used to label questions or answers (posts) that have inappropriate or offensive content and should not be used just because a user does not agree with the content of the post.
              If a post receives enough flags, it is moved to a ‘doghouse’. All the posts in the doghouse are visible to all the students but not to the teacher. Once a student presses a flag button,
              the flag color changes to red only on that student’s page.
              <br />
              <br />
              A flagged post can be moved back to the regular Q&A area if enough students do not think that the post should be in the doghouse. Every time a post goes to the doghouse, the reputation
              of the person who made a post is reduced. A person with a sufficiently low reputation is not allowed to make a new post. However, reputation can be regained over time. All this is
              calculated by the algorithm.
              <br />
              <br />
              Please use flagging judiciously. If you flag to many posts and many other users disagree with you, your reputation might be decreased.
            </CreateTextWeb>
          </>
        )}
      </Wrapper>

      <Wrapper>
        {isMobile ? (
          <>
            <CreateTitleMob title="Some icons/buttons of interest:" />
            <Box sx={{ display: "flex", pb: 1.5 }}>
              <CreateImage width={2.1} image={Images.NEW_LOCK} />
            </Box>
            <CreateTextMob>
              {boldText("Lock icon")} on Q/As cards means that you can only view a Q/A and not interact with it (e.g. post answers or enter discussions). This can happen if you are viewing Q/As from
              the lectures that you did not attend and therefore have not received a pseudonym for that lecture.
            </CreateTextMob>
            <br />
            <Box sx={{ display: "flex", pb: 1.5 }}>
              <CreateImage width={11.4} image={Images.NEW_LIVE} />
            </Box>
            <CreateTextMob>
              {boldText("Live button")} appears in the header if there is live lecture for the course for which you have registered. Pressing the button will take you to the remote control (if you
              have a pseudonym for that lecture), or to the page to enter the Lecture Code and obtain a pseudonym.
            </CreateTextMob>
            <br />
            <Box sx={{ display: "flex", pb: 1.5 }}>
              <CreateImage width={3.7} image={Images.NEW_HOW_AM_I} />
            </Box>
            <CreateTextMob>
              {boldText("Who am I?")} Since students are assigned a new pseudonym for every lecture they attend, it might be hard to track your virtual identity especially if you are switching
              lectures. To find out your current pseudonym, press the ‘Who am I?’ button.
            </CreateTextMob>
            <br />
            <Box sx={{ display: "flex", pb: 1.5 }}>
              <CreateImage width={20} image={Images.NEW_JOIN_LECTURE} />
            </Box>
            <CreateTextMob>
              {boldText("Join Lecture")}. Clicking on this button will take you to the page where you can type a Lecture Code to join a live lecture. This button also allows students to switch from
              one lecture to another. However, a student can attend only one live lecture at a time. If for example you are participating in a live lecture, and decide to join another live lecture
              (e.g., from another course), you will be signed out from the first live lecture.
            </CreateTextMob>
          </>
        ) : (
          <>
            <CreateTitleWeb title="Some icons/buttons of interest:" />
            <Box sx={{ display: "flex", pb: 1.5 }}>
              <CreateImage width={2.1} image={Images.NEW_LOCK} />
            </Box>
            <CreateTextWeb>
              {boldText("Lock icon")} on Q/As cards means that you can only view a Q/A and not interact with it (e.g. post answers or enter discussions). This can happen if you are viewing Q/As from
              the lectures that you did not attend and therefore have not received a pseudonym for that lecture.
            </CreateTextWeb>
            <br />
            <Box sx={{ display: "flex", pb: 1.5 }}>
              <CreateImage width={11.4} image={Images.NEW_LIVE} />
            </Box>
            <CreateTextWeb>
              {boldText("Live button")} appears in the header if there is live lecture for the course for which you have registered. Pressing the button will take you to the remote control (if you
              have a pseudonym for that lecture), or to the page to enter the Lecture Code and obtain a pseudonym.
            </CreateTextWeb>
            <br />
            <Box sx={{ display: "flex", pb: 1.5 }}>
              <CreateImage width={3.7} image={Images.NEW_HOW_AM_I} />
            </Box>
            <CreateTextWeb>
              {boldText("Who am I?")} Since students are assigned a new pseudonym for every lecture they attend, it might be hard to track your virtual identity especially if you are switching
              lectures. To find out your current pseudonym, press the ‘Who am I?’ button.
            </CreateTextWeb>
            <br />
            <Box sx={{ display: "flex", pb: 1.5 }}>
              <CreateImage width={20} image={Images.NEW_JOIN_LECTURE} />
            </Box>
            <CreateTextWeb>
              {boldText("Join Lecture")}. Clicking on this button will take you to the page where you can type a Lecture Code to join a live lecture. This button also allows students to switch from
              one lecture to another. However, a student can attend only one live lecture at a time. If for example you are participating in a live lecture, and decide to join another live lecture
              (e.g., from another course), you will be signed out from the first live lecture.
            </CreateTextWeb>
          </>
        )}
      </Wrapper>
    </Stack>
  );
}

const Wrapper = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      border: "1px solid " + Colors.LIGHT_BLUE_HEADER,
      borderRadius: "15px",
      padding: 2,
    }}
  >
    {children}
  </Box>
);

const CreateTitleMob = ({ title, textAlign }: { title: string; textAlign?: any }) => (
  <Typography textAlign={textAlign ?? "center"} pb={1} fontWeight="bold" color={Colors.ANSWER} variant="body2">
    {title}
  </Typography>
);
const CreateTitleWeb = ({ title, textAlign }: { title: string; textAlign?: any }) => (
  <Typography textAlign={textAlign ?? "left"} pb={1} fontWeight="bold" color={Colors.ANSWER} variant="body1">
    {title}
  </Typography>
);

const CreateTextMob = ({ children }: any) => <Typography fontSize={12}>{children}</Typography>;
const CreateTextWeb = ({ children }: any) => <Typography fontSize={14}>{children}</Typography>;
